import {
  AgentV2ConversationDetailResponse,
  AgentV2PersistedTurn,
  AgentV2PersistedTurnEventEnvelope,
  AgentV2RunFailureCode,
  AgentV2ServerEventEnvelope,
  AgentV2ServerEventType,
  MatterArtifactVersionSelection,
} from './schemas'
import { MatterArtifactVersionRevisionKind, MatterArtifactVersionStatus, type MatterArtifactVersionRead } from '@/matter-artifacts/schemas'
import type { DocxExportNotice } from '@/agent-v2/docx-export/schemas'

export type AgentV2ProgressRow = {
  kind: 'update'
  id: string
  label: string
  sequence: number
  status: 'active' | 'complete' | 'cancelled' | 'error'
}

export type AgentV2WorkLogItem = AgentV2ProgressRow

/** The user files a turn was sent with, as saved on the turn's search defaults. */
export type AgentV2MessageAttachedFiles = {
  fileIds: string[]
  writingSampleFileId: string | null
}

export type AgentV2Message = {
  id: string
  clientRequestId?: string
  role: 'assistant' | 'user'
  text: string
  turnId?: string
  matterArtifactSelection?: MatterArtifactVersionSelection
  /* User messages only. Undefined when the transcript has not seen the turn's search defaults yet. */
  attachedFiles?: AgentV2MessageAttachedFiles
  addedFiles?: AgentV2MessageAttachedFiles
  // Display-only snapshots from this browser's submissions; never sent to the API.
  attachedSkillNames?: string[]
  // The Matter artifacts slice owns the version data; messages keep only the association (ENG-7073)
  matterArtifactVersionIds?: string[]
  // Live creation hints only; opening saved history must not trigger navigation.
  createdTableIds?: string[]
  tableCardIds?: string[]
  tableViewUpdateIds?: Record<string, string>
  workLogItems?: AgentV2WorkLogItem[]
  runStartedAt?: string
  runEndedAt?: string
  pendingTextTokens?: string[]
  isTextComplete?: boolean
  isStreaming?: boolean
  isError?: boolean
  errorText?: string
  isStopped?: boolean
  canReceiveFeedback?: boolean
  // A server-written Export as .docx notice; the chat shows it as a card instead of the text.
  docxExport?: DocxExportNotice
}

export type AgentV2ChatState = {
  messages: AgentV2Message[]
  isRunning: boolean
  // A stop command was accepted by the live transport, but the run has not yet
  // acknowledged a terminal outcome through a live event or durable hydration.
  isStopping: boolean
  // The socket died while a server run may still be active: submit stays blocked
  // until a fresh conversation detail shows the latest turn ended (ENG-7068)
  isRecovering: boolean
  errorMessage: string | null
  streamingMessageId: string | null
  activeAssistantMessageId: string | null
  seenEventIds: Record<string, boolean>
}

// Route-independent transcript state. Run/recovery phases intentionally live in
// agentV2ConversationsState; this state owns only rendered chat data and the
// bounded metadata needed to reconcile live events with durable snapshots.
export type AgentV2TranscriptState = {
  messages: AgentV2Message[]
  errorMessage: string | null
  streamingMessageId: string | null
  activeAssistantMessageId: string | null
  seenEventIdsByTurnId: Record<string, Record<string, boolean> | undefined>
  lastAssistantDeltaSequenceByTurnId: Record<string, number | undefined>
  lastDurableUpdatedAt: string | null
  latestDurableTurnId: string | null
  latestTerminalTurnId: string | null
}

export const kAgentV2InitialState: AgentV2ChatState = {
  messages: [],
  isRunning: false,
  isStopping: false,
  isRecovering: false,
  errorMessage: null,
  streamingMessageId: null,
  activeAssistantMessageId: null,
  seenEventIds: {},
}

export const kAgentV2InitialTranscriptState: AgentV2TranscriptState = {
  messages: [],
  errorMessage: null,
  streamingMessageId: null,
  activeAssistantMessageId: null,
  seenEventIdsByTurnId: {},
  lastAssistantDeltaSequenceByTurnId: {},
  lastDurableUpdatedAt: null,
  latestDurableTurnId: null,
  latestTerminalTurnId: null,
}

const kRunFailedDisplayMessage = 'Something went wrong while preparing the response. Please try again.'
const kAttachedFilesUnavailableDisplayMessage = 'One or more selected files are unavailable or still processing. Refresh your files and try again.'

type AgentV2ReducerEvent = AgentV2ServerEventEnvelope | AgentV2PersistedTurnEventEnvelope

export type AgentV2ChatAction =
  | {
      type: 'submit'
      clientRequestId?: string
      inputText: string
      attachedSkillNames?: string[]
      matterArtifactSelection?: MatterArtifactVersionSelection
      attachedFiles?: AgentV2MessageAttachedFiles
      userMessageId: string
      assistantMessageId: string
    }
  | { type: 'flushTextToken'; messageId: string; tokenCount?: number }
  | { type: 'serverEvent'; event: AgentV2ServerEventEnvelope }
  | { type: 'hydrateFromPersistedConversation'; conversation: AgentV2ConversationDetailResponse }
  | { type: 'stopRequested' }
  | { type: 'socketError'; message: string; recovering?: boolean }
  | { type: 'runStoppedBeforeStart' }

export type AgentV2TranscriptAction =
  | { type: 'prependPersistedHistory'; conversation: AgentV2ConversationDetailResponse }
  | {
      type: 'turnSubmitted'
      clientRequestId: string
      inputText: string
      attachedSkillNames?: string[]
      matterArtifactSelection?: MatterArtifactVersionSelection
      attachedFiles?: AgentV2MessageAttachedFiles
      userMessageId: string
      assistantMessageId: string
    }
  | { type: 'serverEventReceived'; event: AgentV2ServerEventEnvelope }
  | { type: 'hydrateFromPersistedConversation'; conversation: AgentV2ConversationDetailResponse }
  | { type: 'transportDetached' }
  | { type: 'turnFailedBeforeAdmission'; message: string }
  | { type: 'runStoppedBeforeStart' }
  | {
      type: 'matterArtifactEditAccepted'
      turnId: string
      resultMatterArtifactVersionId: string
    }

export function agentV2ChatReducer(state: AgentV2ChatState, action: AgentV2ChatAction): AgentV2ChatState {
  switch (action.type) {
    case 'submit':
      return {
        ...state,
        messages: [
          // The prior answer may still be typing out; fold its pending tokens into the
          // text so moving streamingMessageId to the new turn cannot truncate it (ENG-7068)
          ...foldPendingAssistantText(state.messages, state.streamingMessageId),
          {
            id: action.userMessageId,
            clientRequestId: action.clientRequestId,
            role: 'user',
            text: action.inputText,
            attachedSkillNames: action.attachedSkillNames,
            ...(action.matterArtifactSelection ? { matterArtifactSelection: action.matterArtifactSelection } : {}),
            ...(action.attachedFiles ? { attachedFiles: action.attachedFiles } : {}),
          },
          { id: action.assistantMessageId, clientRequestId: action.clientRequestId, role: 'assistant', text: '', workLogItems: [], isStreaming: true },
        ],
        isRunning: true,
        isStopping: false,
        isRecovering: false,
        errorMessage: null,
        streamingMessageId: action.assistantMessageId,
        activeAssistantMessageId: action.assistantMessageId,
      }

    case 'serverEvent':
      return applyServerEventOnce(state, action.event)

    case 'hydrateFromPersistedConversation': {
      const hydratedState = hydrateFromPersistedConversation(action.conversation)
      const reconciledState = state.isRecovering ? reconcileTurnRecoveryState(state, hydratedState, action.conversation) : hydratedState
      if (!reconciledState.isRecovering) return reconciledState
      if (!state.isStopping) return reconciledState

      // An open durable snapshot cannot disprove that stop was requested locally.
      return {
        ...reconciledState,
        isStopping: true,
      }
    }

    case 'flushTextToken': {
      const messages = flushMessageTextTokens(state.messages, action.messageId, action.tokenCount ?? 1)
      return {
        ...state,
        messages,
        streamingMessageId: getStreamingMessageIdAfterFlush(messages, action.messageId, state.streamingMessageId),
      }
    }

    case 'stopRequested':
      if (!state.isRunning || state.isStopping) return state
      return {
        ...state,
        isStopping: true,
        errorMessage: null,
      }

    case 'socketError':
      if (action.recovering) {
        return {
          ...state,
          isRunning: false,
          isRecovering: true,
          errorMessage: null,
          // Keep work-log rows semantically active while durable recovery determines
          // the run outcome. Already-delivered answer text
          // remains visible instead of being relabelled as a connection failure.
          messages: foldPendingAssistantText(state.messages, state.streamingMessageId),
          activeAssistantMessageId: null,
          streamingMessageId: null,
        }
      }

      return {
        ...state,
        isRunning: false,
        isStopping: false,
        isRecovering: false,
        errorMessage: state.activeAssistantMessageId ? null : action.message,
        messages: failAssistantMessage(
          markMessageWorkLogError(clearAssistantPendingText(state.messages, state.streamingMessageId), state.activeAssistantMessageId),
          state.activeAssistantMessageId,
          action.message
        ),
        activeAssistantMessageId: null,
        streamingMessageId: null,
      }

    case 'runStoppedBeforeStart':
      return {
        ...state,
        isRunning: false,
        isStopping: false,
        isRecovering: false,
        errorMessage: null,
        messages: stopAssistantMessage(state.messages, state.streamingMessageId, state.activeAssistantMessageId),
        activeAssistantMessageId: null,
        streamingMessageId: null,
      }
  }
}

export function agentV2TranscriptReducer(state: AgentV2TranscriptState, action: AgentV2TranscriptAction): AgentV2TranscriptState {
  switch (action.type) {
    case 'prependPersistedHistory': {
      const existingTurnIds = new Set(state.messages.map((message) => message.turnId))
      const history = hydrateFromPersistedConversation({
        ...action.conversation,
        turns: action.conversation.turns.filter((turn) => !existingTurnIds.has(turn.id)),
      })
      return { ...state, messages: [...history.messages, ...state.messages] }
    }
    case 'turnSubmitted':
      return fromChatState(
        agentV2ChatReducer(toChatState(state), {
          type: 'submit',
          clientRequestId: action.clientRequestId,
          inputText: action.inputText,
          attachedSkillNames: action.attachedSkillNames,
          matterArtifactSelection: action.matterArtifactSelection,
          attachedFiles: action.attachedFiles,
          userMessageId: action.userMessageId,
          assistantMessageId: action.assistantMessageId,
        }),
        state
      )

    case 'serverEventReceived':
      return reduceTranscriptServerEvent(state, action.event)

    case 'hydrateFromPersistedConversation':
      return reconcileTranscriptFromPersistedConversation(state, action.conversation)

    case 'transportDetached':
      return fromChatState(agentV2ChatReducer(toChatState(state), { type: 'socketError', message: '', recovering: true }), state)

    case 'turnFailedBeforeAdmission':
      return fromChatState(agentV2ChatReducer(toChatState(state), { type: 'socketError', message: action.message }), state)

    case 'runStoppedBeforeStart':
      return fromChatState(agentV2ChatReducer(toChatState(state), { type: 'runStoppedBeforeStart' }), state)

    case 'matterArtifactEditAccepted': {
      const messageId = state.messages.find((message) => message.role === 'assistant' && message.turnId === action.turnId)?.id ?? null
      return {
        ...state,
        messages: storeMessageMatterArtifactVersionIds(state.messages, messageId, [action.resultMatterArtifactVersionId]),
      }
    }
  }
}

function reduceTranscriptServerEvent(state: AgentV2TranscriptState, event: AgentV2ServerEventEnvelope): AgentV2TranscriptState {
  if (event.type === AgentV2ServerEventType.RUN_STARTED) {
    const activeAssistant = state.messages.find((message) => message.id === state.activeAssistantMessageId)
    if (activeAssistant?.clientRequestId !== event.payload.client_request_id) return state
  }

  if (event.type === AgentV2ServerEventType.ASSISTANT_MESSAGE_DELTA) {
    const lastSequence = state.lastAssistantDeltaSequenceByTurnId[event.turn_id]
    if (lastSequence !== undefined && event.sequence <= lastSequence) return state
  } else if (state.seenEventIdsByTurnId[event.turn_id]?.[event.event_id]) {
    return state
  }

  let nextChatState = agentV2ChatReducer(toChatState(state), { type: 'serverEvent', event })
  // The backend already coalesces deltas into bounded frames. Append the validated
  // batch immediately instead of retaining a route-owned word-at-a-time queue.
  if (event.type === AgentV2ServerEventType.ASSISTANT_MESSAGE_DELTA && nextChatState.streamingMessageId) {
    nextChatState = agentV2ChatReducer(nextChatState, {
      type: 'flushTextToken',
      messageId: nextChatState.streamingMessageId,
      tokenCount: Number.POSITIVE_INFINITY,
    })
  }

  let nextState = canonicalizeTurnMessageIds(fromChatState(nextChatState, state), event.turn_id)
  if (event.type === AgentV2ServerEventType.ASSISTANT_MESSAGE_DELTA) {
    nextState = {
      ...nextState,
      lastAssistantDeltaSequenceByTurnId: {
        ...nextState.lastAssistantDeltaSequenceByTurnId,
        [event.turn_id]: event.sequence,
      },
    }
  } else {
    nextState = {
      ...nextState,
      seenEventIdsByTurnId: {
        ...nextState.seenEventIdsByTurnId,
        [event.turn_id]: {
          ...nextState.seenEventIdsByTurnId[event.turn_id],
          [event.event_id]: true,
        },
      },
    }
  }

  if (!isTerminalEventType(event.type)) return nextState

  const { [event.turn_id]: _seenTerminalTurn, ...seenEventIdsByTurnId } = nextState.seenEventIdsByTurnId
  const { [event.turn_id]: _terminalDeltaSequence, ...lastAssistantDeltaSequenceByTurnId } = nextState.lastAssistantDeltaSequenceByTurnId
  return {
    ...nextState,
    seenEventIdsByTurnId,
    lastAssistantDeltaSequenceByTurnId,
    latestTerminalTurnId: event.turn_id,
  }
}

function reconcileTranscriptFromPersistedConversation(state: AgentV2TranscriptState, conversation: AgentV2ConversationDetailResponse): AgentV2TranscriptState {
  if (isOlderDurableSnapshot(state.lastDurableUpdatedAt, conversation.conversation.updated_at)) return state

  const terminalRegression = conversation.turns.some((turn) => turn.id === state.latestTerminalTurnId && turn.ended_at === null)
  if (terminalRegression) return state

  // The chat reducer contains the established message/progress/failure semantics.
  // Supplying recovering=true asks it to merge open-turn live data instead of
  // replacing it. The Redux transcript itself does not retain that lifecycle flag.
  const hydratedChatState = agentV2ChatReducer(toChatState(state, { isRunning: false, isStopping: false, isRecovering: true }), {
    type: 'hydrateFromPersistedConversation',
    conversation,
  })
  let nextState = fromChatState(hydratedChatState, state)
  nextState = preserveLiveMessagesMissingFromSnapshot(state, nextState, conversation)
  nextState = preserveOpenLivePointers(state, nextState, conversation)

  const openTurnIds = new Set(conversation.turns.filter((turn) => turn.ended_at === null).map((turn) => turn.id))
  const seenEventIdsByTurnId: AgentV2TranscriptState['seenEventIdsByTurnId'] = {}
  const lastAssistantDeltaSequenceByTurnId: AgentV2TranscriptState['lastAssistantDeltaSequenceByTurnId'] = {}

  for (const [turnId, eventIds] of Object.entries(state.seenEventIdsByTurnId)) {
    if (openTurnIds.has(turnId) || !conversation.turns.some((turn) => turn.id === turnId)) seenEventIdsByTurnId[turnId] = eventIds
  }
  for (const turn of conversation.turns) {
    if (turn.ended_at !== null) continue
    seenEventIdsByTurnId[turn.id] = {
      ...seenEventIdsByTurnId[turn.id],
      ...Object.fromEntries(turn.events.map((event) => [event.id, true])),
    }
  }
  for (const [turnId, sequence] of Object.entries(state.lastAssistantDeltaSequenceByTurnId)) {
    if (openTurnIds.has(turnId) || !conversation.turns.some((turn) => turn.id === turnId)) lastAssistantDeltaSequenceByTurnId[turnId] = sequence
  }

  const latestTurn = [...conversation.turns].sort((a, b) => Date.parse(a.started_at) - Date.parse(b.started_at)).at(-1)
  return {
    ...nextState,
    seenEventIdsByTurnId,
    lastAssistantDeltaSequenceByTurnId,
    lastDurableUpdatedAt: conversation.conversation.updated_at,
    latestDurableTurnId: latestTurn?.id ?? null,
    latestTerminalTurnId: latestTurn?.ended_at ? latestTurn.id : state.latestTerminalTurnId,
  }
}

function toChatState(
  state: AgentV2TranscriptState,
  lifecycle: Pick<AgentV2ChatState, 'isRunning' | 'isStopping' | 'isRecovering'> = {
    isRunning: false,
    isStopping: false,
    isRecovering: false,
  }
): AgentV2ChatState {
  return {
    messages: state.messages,
    errorMessage: state.errorMessage,
    streamingMessageId: state.streamingMessageId,
    activeAssistantMessageId: state.activeAssistantMessageId,
    seenEventIds: Object.assign({}, ...Object.values(state.seenEventIdsByTurnId).filter(Boolean)),
    ...lifecycle,
  }
}

function fromChatState(
  state: AgentV2ChatState,
  metadata: Pick<
    AgentV2TranscriptState,
    'seenEventIdsByTurnId' | 'lastAssistantDeltaSequenceByTurnId' | 'lastDurableUpdatedAt' | 'latestDurableTurnId' | 'latestTerminalTurnId'
  >
): AgentV2TranscriptState {
  return {
    messages: state.messages,
    errorMessage: state.errorMessage,
    streamingMessageId: state.streamingMessageId,
    activeAssistantMessageId: state.activeAssistantMessageId,
    seenEventIdsByTurnId: metadata.seenEventIdsByTurnId,
    lastAssistantDeltaSequenceByTurnId: metadata.lastAssistantDeltaSequenceByTurnId,
    lastDurableUpdatedAt: metadata.lastDurableUpdatedAt,
    latestDurableTurnId: metadata.latestDurableTurnId,
    latestTerminalTurnId: metadata.latestTerminalTurnId,
  }
}

function hydrateFromPersistedConversation(conversation: AgentV2ConversationDetailResponse): AgentV2ChatState {
  const turns = [...conversation.turns].sort((a, b) => Date.parse(a.started_at) - Date.parse(b.started_at) || a.id.localeCompare(b.id))
  const assistantMessageIdByTurnId = new Map<string, string>()

  const messages = turns.map((turn): AgentV2Message => {
    const assistantMessageId = `${turn.id}:assistant`
    assistantMessageIdByTurnId.set(turn.id, assistantMessageId)

    return {
      id: assistantMessageId,
      clientRequestId: turn.client_request_id,
      role: 'assistant',
      text: '',
      turnId: turn.id,
      workLogItems: [],
      runStartedAt: turn.started_at,
      runEndedAt: turn.ended_at ?? undefined,
      pendingTextTokens: [],
      isStreaming: false,
      isTextComplete: true,
      canReceiveFeedback: Boolean(turn.ended_at && !turn.did_fail && !turn.did_cancel),
    }
  })

  let nextState: AgentV2ChatState = {
    ...kAgentV2InitialState,
    messages,
  }

  for (const turn of turns) {
    const messageId = assistantMessageIdByTurnId.get(turn.id) ?? null

    for (const event of orderPersistedTurnEventsForHydration(turn.events)) {
      nextState = applyServerEventOnce(
        {
          ...nextState,
          activeAssistantMessageId: messageId,
          streamingMessageId: messageId,
        },
        event
      )
    }

    nextState = {
      ...nextState,
      messages: storeMessageMatterArtifactVersionIds(
        nextState.messages,
        messageId,
        getChatMatterArtifactVersionIds(nextState.messages, turn.id, turn.artifact_versions)
      ),
    }

    nextState = {
      ...nextState,
      messages: applyPersistedTurnTerminalState(storeUserMessageAttachedFiles(nextState.messages, turn), messageId, turn),
    }
  }

  // A latest turn that has not ended is still running server-side: hydrate into recovery so
  // submit stays disabled until the turn is observed terminal — otherwise a reload mid-run
  // could start a second concurrent turn for the same conversation (PR #165 review)
  // Server-written notice turns end when written, so they can follow a still-running user turn.
  const latestOpenTurn = turns.filter((turn) => turn.ended_at === null).at(-1)
  return {
    ...nextState,
    messages: finalizeHistoricalMessages(nextState.messages, latestOpenTurn?.id ?? null),
    isRunning: false,
    isRecovering: Boolean(latestOpenTurn),
    errorMessage: null,
    activeAssistantMessageId: null,
    streamingMessageId: null,
  }
}

function reconcileTurnRecoveryState(
  localState: AgentV2ChatState,
  hydratedState: AgentV2ChatState,
  conversation: AgentV2ConversationDetailResponse
): AgentV2ChatState {
  const turnById = new Map(conversation.turns.map((turn) => [turn.id, turn]))
  const localAssistantByTurnId = new Map(
    localState.messages
      .filter((message): message is AgentV2Message & { turnId: string } => message.role === 'assistant' && Boolean(message.turnId))
      .map((message) => [message.turnId, message])
  )

  return {
    ...hydratedState,
    messages: hydratedState.messages.map((message) => {
      if (message.role === 'user' && message.turnId) {
        const clientRequestId = turnById.get(message.turnId)?.client_request_id
        const localMessage = localState.messages.find(
          (local) => local.role === 'user' && (local.turnId === message.turnId || (clientRequestId && local.clientRequestId === clientRequestId))
        )
        return localMessage?.attachedSkillNames ? { ...message, attachedSkillNames: localMessage.attachedSkillNames } : message
      }
      if (message.role !== 'assistant' || !message.turnId) return message

      const localMessage = localAssistantByTurnId.get(message.turnId)
      const turn = turnById.get(message.turnId)
      if (!localMessage || !turn) return message

      if (turn.ended_at !== null) {
        // A stale-turn reconciler can close an abandoned run without a durable
        // assistant checkpoint. Keep text already shown in this browser tab, while
        // the hydrated terminal flags remain authoritative for failure/cancellation.
        if ((!turn.did_fail && !turn.did_cancel) || message.text || !localMessage.text) return message

        const workLogItems = reconcileRecoveredWorkLogItems(localMessage.workLogItems ?? [], message.workLogItems ?? [])
        return {
          ...message,
          id: localMessage.id,
          text: localMessage.text,
          tableCardIds: localMessage.tableCardIds,
          workLogItems: turn.did_fail ? markWorkLogError(workLogItems) : markProgressRowsCancelled(workLogItems),
        }
      }

      return {
        ...message,
        id: localMessage.id,
        text: reconcileRecoveredAssistantText(localMessage.text, message.text),
        tableCardIds: [...new Set([...(localMessage.tableCardIds ?? []), ...(message.tableCardIds ?? [])])],
        matterArtifactVersionIds: [...new Set([...(localMessage.matterArtifactVersionIds ?? []), ...(message.matterArtifactVersionIds ?? [])])],
        workLogItems: reconcileRecoveredWorkLogItems(localMessage.workLogItems ?? [], message.workLogItems ?? []),
      }
    }),
    seenEventIds: {
      ...localState.seenEventIds,
      ...hydratedState.seenEventIds,
    },
  }
}

function reconcileRecoveredAssistantText(localText: string, hydratedText: string): string {
  if (!localText) return hydratedText
  if (!hydratedText || localText.startsWith(hydratedText)) return localText
  if (hydratedText.startsWith(localText)) return hydratedText

  // Open-turn deltas are intentionally live-only. If the two views cannot be
  // ordered safely, retain the text the user has already seen until terminal
  // hydration supplies the authoritative completed assistant message.
  return localText
}

function reconcileRecoveredWorkLogItems(localItems: AgentV2WorkLogItem[], hydratedItems: AgentV2WorkLogItem[]): AgentV2WorkLogItem[] {
  const itemById = new Map(localItems.map((item) => [`${item.kind}:${item.id}`, item]))

  for (const hydratedItem of hydratedItems) {
    const key = `${hydratedItem.kind}:${hydratedItem.id}`
    itemById.set(key, hydratedItem)
  }

  return sortWorkLogItems([...itemById.values()])
}

function applyPersistedTurnTerminalState(messages: AgentV2Message[], messageId: string | null, turn: AgentV2PersistedTurn): AgentV2Message[] {
  if (!messageId) return messages

  if (turn.did_fail) {
    return failAssistantMessage(markMessageWorkLogError(messages, messageId), messageId, kRunFailedDisplayMessage)
  }

  if (turn.did_cancel) {
    return stopAssistantMessage(messages, messageId, messageId)
  }

  if (turn.ended_at) {
    return markMessageProgressRowsComplete(messages, messageId)
  }

  return messages
}

function orderPersistedTurnEventsForHydration(events: AgentV2PersistedTurn['events']): AgentV2PersistedTurn['events'] {
  return events
    .map((event, index) => ({ event, index }))
    .sort((a, b) => {
      if (a.event.turn_id === b.event.turn_id && a.event.sequence !== b.event.sequence) return a.event.sequence - b.event.sequence
      return a.index - b.index
    })
    .map(({ event }) => event)
}

function applyServerEventOnce(state: AgentV2ChatState, event: AgentV2ReducerEvent): AgentV2ChatState {
  // seenEventIds dedupes redelivery of the same live frame, persisted row, or semantic run event
  // observed through both live delivery and hydration. Transport-only deltas and terminally
  // synthesized assistant messages still require durable snapshot reconciliation during
  // REST recovery after a detached one-turn socket (ENG-7076).
  const eventId = getReducerEventId(event)
  if (state.seenEventIds[eventId]) return state

  const nextState = applyServerEvent(state, event)
  return markServerEventSeen(nextState, eventId)
}

function markServerEventSeen(state: AgentV2ChatState, eventId: string): AgentV2ChatState {
  return {
    ...state,
    seenEventIds: {
      ...state.seenEventIds,
      [eventId]: true,
    },
  }
}

function applyServerEvent(state: AgentV2ChatState, event: AgentV2ReducerEvent): AgentV2ChatState {
  let stateWithTurnId = assignTurnIdToEventMessage(state, event)

  if (
    event.type === AgentV2ServerEventType.RUN_STARTED ||
    event.type === AgentV2ServerEventType.RUN_COMPLETED ||
    event.type === AgentV2ServerEventType.RUN_CANCELLED ||
    event.type === AgentV2ServerEventType.RUN_FAILED
  ) {
    stateWithTurnId = {
      ...stateWithTurnId,
      messages: stateWithTurnId.messages.map((message) =>
        message.role === 'assistant' && message.turnId === event.turn_id
          ? { ...message, ...(event.type === AgentV2ServerEventType.RUN_STARTED ? { runStartedAt: event.created_at } : { runEndedAt: event.created_at }) }
          : message
      ),
    }
  }

  switch (event.type) {
    case AgentV2ServerEventType.RUN_STARTED:
      return {
        ...stateWithTurnId,
        isRunning: true,
        errorMessage: null,
      }

    case AgentV2ServerEventType.INTERACTION_UPDATE:
      return {
        ...stateWithTurnId,
        messages: appendMessageProgressRow(stateWithTurnId.messages, stateWithTurnId.activeAssistantMessageId, {
          kind: 'update',
          id: getReducerEventId(event),
          label: event.payload.message,
          sequence: event.sequence,
          status: 'active',
        }),
      }

    case AgentV2ServerEventType.TABLE_VIEW_UPDATED:
      return {
        ...stateWithTurnId,
        messages: stateWithTurnId.messages.map((message) =>
          message.id === stateWithTurnId.activeAssistantMessageId
            ? { ...message, tableViewUpdateIds: { ...message.tableViewUpdateIds, [event.payload.table_id]: getReducerEventId(event) } }
            : message
        ),
      }

    case AgentV2ServerEventType.TABLE_CREATED:
    case AgentV2ServerEventType.TABLE_CARD_ADDED:
      return {
        ...stateWithTurnId,
        messages: stateWithTurnId.messages.map((message) =>
          message.id === stateWithTurnId.activeAssistantMessageId
            ? {
                ...message,
                tableCardIds: [...new Set([...(message.tableCardIds ?? []), event.payload.table_id])],
                ...(event.type === AgentV2ServerEventType.TABLE_CREATED
                  ? { createdTableIds: [...new Set([...(message.createdTableIds ?? []), event.payload.table_id])] }
                  : {}),
              }
            : message
        ),
      }

    // The label is derived here rather than sent, so the instruction text never crosses the wire.
    // Live-only: the durable record is the assistant's own message saying what it saved.
    case AgentV2ServerEventType.CUSTOM_INSTRUCTIONS_UPDATED:
      return {
        ...stateWithTurnId,
        messages: appendMessageProgressRow(stateWithTurnId.messages, stateWithTurnId.activeAssistantMessageId, {
          kind: 'update',
          id: getReducerEventId(event),
          label: event.payload.cleared ? 'Cleared your custom instructions' : 'Updated your custom instructions',
          sequence: event.sequence,
          status: 'complete',
        }),
      }

    case AgentV2ServerEventType.USER_MESSAGE_CREATED:
      return {
        ...stateWithTurnId,
        messages: setUserCreatedMessage(stateWithTurnId.messages, event),
      }

    case AgentV2ServerEventType.ASSISTANT_MESSAGE_DELTA:
      return {
        ...stateWithTurnId,
        messages: enqueueAssistantText(stateWithTurnId.messages, stateWithTurnId.streamingMessageId, event.payload.content),
      }

    case AgentV2ServerEventType.ASSISTANT_MESSAGE_CREATED:
      return {
        ...stateWithTurnId,
        messages: setAssistantCreatedMessage(stateWithTurnId.messages, getMessageIdForServerEvent(stateWithTurnId, event), event.payload),
      }

    case AgentV2ServerEventType.ASSISTANT_MESSAGE_COMPLETED: {
      const messages = completeAssistantMessage(stateWithTurnId.messages, stateWithTurnId.streamingMessageId)
      return {
        ...stateWithTurnId,
        messages,
        streamingMessageId: hasPendingTextTokens(messages, stateWithTurnId.streamingMessageId) ? stateWithTurnId.streamingMessageId : null,
      }
    }

    // Reference resolution lives in the Redux agentV2ReferencesState slice (the socket
    // layer dispatches the upsert); messages keep no reference state — message text is
    // the source of truth and rendering resolves extracted citation ids against the map.
    case AgentV2ServerEventType.ASSISTANT_MESSAGE_REFERENCES:
      return stateWithTurnId

    // Live-only lifecycle telemetry from the matter-record update tools; the progress UI for
    // these is not built yet, so accept-and-ignore keeps the socket alive (PR #161 review).
    case AgentV2ServerEventType.LIVING_ASSET_UPDATE_STARTED:
    case AgentV2ServerEventType.LIVING_ASSET_UPDATE_PROGRESS:
    case AgentV2ServerEventType.LIVING_ASSET_UPDATE_COMPLETED:
    case AgentV2ServerEventType.LIVING_ASSET_UPDATE_FAILED:
    case AgentV2ServerEventType.LIVING_ASSET_UPDATE_CANCELLED:
    case AgentV2ServerEventType.USER_SKILL_SAVED:
    case AgentV2ServerEventType.MATTER_ARTIFACT_EDIT_PROPOSED:
      return stateWithTurnId

    case AgentV2ServerEventType.DRAFT_STARTED:
    case AgentV2ServerEventType.DRAFT_COMPLETED:
    case AgentV2ServerEventType.DRAFT_FAILED:
    case AgentV2ServerEventType.DRAFT_CANCELLED:
      return {
        ...stateWithTurnId,
        messages: storeMessageMatterArtifactVersionIds(
          stateWithTurnId.messages,
          getMessageIdForServerEvent(stateWithTurnId, event),
          getChatMatterArtifactVersionIds(stateWithTurnId.messages, event.turn_id, [event.payload.artifact_version])
        ),
      }

    case AgentV2ServerEventType.RUN_COMPLETED: {
      const completedMessages = completeAssistantMessage(stateWithTurnId.messages, stateWithTurnId.streamingMessageId)
      const messages = setMessageFeedbackEligibility(
        markMessageProgressRowsComplete(completedMessages, stateWithTurnId.activeAssistantMessageId),
        stateWithTurnId.activeAssistantMessageId,
        true
      )
      return {
        ...stateWithTurnId,
        isRunning: false,
        isStopping: false,
        isRecovering: false,
        messages,
        activeAssistantMessageId: null,
        streamingMessageId: hasPendingTextTokens(messages, stateWithTurnId.streamingMessageId) ? stateWithTurnId.streamingMessageId : null,
      }
    }

    case AgentV2ServerEventType.RUN_CANCELLED:
      return {
        ...stateWithTurnId,
        isRunning: false,
        isStopping: false,
        isRecovering: false,
        messages: stopAssistantMessage(stateWithTurnId.messages, stateWithTurnId.streamingMessageId, stateWithTurnId.activeAssistantMessageId),
        activeAssistantMessageId: null,
        streamingMessageId: null,
      }

    case AgentV2ServerEventType.RUN_FAILED: {
      const failureMessage = getRunFailureDisplayMessage(event.payload.code)
      return {
        ...stateWithTurnId,
        isRunning: false,
        isStopping: false,
        isRecovering: false,
        // With no assistant message to carry the error, fall back to the inline strip
        // rather than fabricating an assistant reply (ENG-7075)
        errorMessage: stateWithTurnId.activeAssistantMessageId ? null : failureMessage,
        messages: failAssistantMessage(
          markMessageWorkLogError(
            clearAssistantPendingText(stateWithTurnId.messages, stateWithTurnId.streamingMessageId),
            stateWithTurnId.activeAssistantMessageId
          ),
          stateWithTurnId.activeAssistantMessageId,
          failureMessage
        ),
        activeAssistantMessageId: null,
        streamingMessageId: null,
      }
    }
  }
}

function getRunFailureDisplayMessage(code: AgentV2RunFailureCode | null | undefined): string {
  if (code === AgentV2RunFailureCode.SELECTED_TABLE_UNAVAILABLE) {
    return 'The selected table is unavailable in this matter. Select an available table and send your message again.'
  }
  if (code === AgentV2RunFailureCode.ATTACHED_FILES_UNAVAILABLE) return kAttachedFilesUnavailableDisplayMessage
  if (code === AgentV2RunFailureCode.MATTER_ARTIFACT_SELECTION_UNAVAILABLE) {
    return 'This document is unavailable for editing. Refresh the conversation and try again.'
  }
  if (code === AgentV2RunFailureCode.MATTER_ARTIFACT_SELECTION_STALE) {
    return 'This selection is out of date. Reopen the latest document version, select the text again, and retry.'
  }
  if (code === AgentV2RunFailureCode.MATTER_ARTIFACT_SELECTION_INVALID) {
    return 'That selection could not be edited. Select the passage again and retry.'
  }
  return kRunFailedDisplayMessage
}

function getReducerEventId(event: AgentV2ReducerEvent): string {
  return 'event_id' in event ? event.event_id : event.id
}

function setUserCreatedMessage(messages: AgentV2Message[], event: Extract<AgentV2ReducerEvent, { type: AgentV2ServerEventType.USER_MESSAGE_CREATED }>) {
  const matterArtifactSelection = event.payload.matter_artifact_selection ?? undefined
  const existingUserMessage = messages.find((message) => message.role === 'user' && message.turnId === event.turn_id)
  if (existingUserMessage) {
    return messages.map((message) =>
      message.id === existingUserMessage.id ? reconcileUserMessage(message, event.payload.content, matterArtifactSelection) : message
    )
  }

  const assistantIndex = messages.findIndex((message) => message.role === 'assistant' && message.turnId === event.turn_id)
  const clientRequestId = assistantIndex >= 0 ? messages[assistantIndex].clientRequestId : undefined
  const optimisticUserIndex = assistantIndex > 0 && messages[assistantIndex - 1]?.role === 'user' ? assistantIndex - 1 : -1
  if (optimisticUserIndex >= 0 && !messages[optimisticUserIndex].turnId && messages[optimisticUserIndex].clientRequestId === clientRequestId) {
    return messages.map((message, index) =>
      index === optimisticUserIndex ? reconcileUserMessage(message, event.payload.content, matterArtifactSelection, event.turn_id) : message
    )
  }

  const userMessage: AgentV2Message = {
    id: `${event.turn_id}:user`,
    clientRequestId,
    role: 'user',
    text: event.payload.content,
    turnId: event.turn_id,
    ...(matterArtifactSelection ? { matterArtifactSelection } : {}),
  }

  if (assistantIndex < 0) return [...messages, userMessage]
  return [...messages.slice(0, assistantIndex), userMessage, ...messages.slice(assistantIndex)]
}

function reconcileUserMessage(
  message: AgentV2Message,
  content: string,
  matterArtifactSelection: MatterArtifactVersionSelection | undefined,
  turnId?: string
): AgentV2Message {
  const nextMessage: AgentV2Message = {
    ...message,
    text: content,
    ...(turnId ? { turnId } : {}),
  }
  if (matterArtifactSelection) nextMessage.matterArtifactSelection = matterArtifactSelection
  else delete nextMessage.matterArtifactSelection
  return nextMessage
}

function setAssistantCreatedMessage(
  messages: AgentV2Message[],
  messageId: string | null,
  payload: {
    content: string
    status: 'completed' | 'cancelled' | 'failed'
    docx_export?: DocxExportNotice | null
    table_card_ids?: string[]
  }
): AgentV2Message[] {
  if (!messageId) return messages

  return messages.map((message) => {
    if (message.id !== messageId) return message

    const baseMessage = {
      ...message,
      text: payload.content,
      tableCardIds: payload.table_card_ids ?? [],
      pendingTextTokens: [],
      isTextComplete: true,
      isStreaming: false,
      // Notices are not model answers, so they take no feedback.
      ...(payload.docx_export ? { docxExport: payload.docx_export, canReceiveFeedback: false } : {}),
    }

    if (payload.status === 'cancelled') {
      return {
        ...baseMessage,
        isStopped: true,
      }
    }

    if (payload.status === 'failed') {
      return {
        ...baseMessage,
        isError: true,
        errorText: kRunFailedDisplayMessage,
      }
    }

    return baseMessage
  })
}

function assignTurnIdToEventMessage(state: AgentV2ChatState, event: AgentV2ReducerEvent): AgentV2ChatState {
  const messageId = getMessageIdForServerEvent(state, event)
  if (!messageId) return state

  return {
    ...state,
    messages: state.messages.map((message) => (message.id === messageId && message.role === 'assistant' ? { ...message, turnId: event.turn_id } : message)),
  }
}

function setMessageFeedbackEligibility(messages: AgentV2Message[], messageId: string | null, canReceiveFeedback: boolean): AgentV2Message[] {
  if (!messageId) return messages
  return messages.map((message) => (message.id === messageId ? { ...message, canReceiveFeedback } : message))
}

function getMessageIdForServerEvent(state: AgentV2ChatState, event: AgentV2ReducerEvent): string | null {
  if (state.activeAssistantMessageId) return state.activeAssistantMessageId
  return state.messages.find((message) => message.role === 'assistant' && message.turnId === event.turn_id)?.id ?? null
}

function storeUserMessageAttachedFiles(messages: AgentV2Message[], turn: AgentV2PersistedTurn): AgentV2Message[] {
  const attachedFiles: AgentV2MessageAttachedFiles = {
    fileIds: turn.search_defaults.attached_file_ids,
    writingSampleFileId: turn.search_defaults.writing_sample_file_id ?? null,
  }
  const addedFiles = turn.added_files
    ? { fileIds: turn.added_files.attached_file_ids, writingSampleFileId: turn.added_files.writing_sample_file_id }
    : undefined
  return messages.map((message) => (message.role === 'user' && message.turnId === turn.id ? { ...message, attachedFiles, addedFiles } : message))
}

function storeMessageMatterArtifactVersionIds(messages: AgentV2Message[], messageId: string | null, matterArtifactVersionIds: string[]): AgentV2Message[] {
  if (!messageId || matterArtifactVersionIds.length === 0) return messages
  return messages.map((message) => {
    if (message.id !== messageId) return message

    return {
      ...message,
      matterArtifactVersionIds: [...new Set([...(message.matterArtifactVersionIds ?? []), ...matterArtifactVersionIds])],
    }
  })
}

function getChatMatterArtifactVersionIds(
  messages: AgentV2Message[],
  turnId: string,
  matterArtifactVersions: ReadonlyArray<Pick<MatterArtifactVersionRead, 'id' | 'matter_artifact_id' | 'revision_kind' | 'status'>>
): string[] {
  const selectedMatterArtifactId = messages.find((message) => message.role === 'user' && message.turnId === turnId)?.matterArtifactSelection?.matter_artifact_id

  // A generating selected-passage result belongs in the open artifact's progress UI, not
  // beside the assistant response. Once complete, its exact turn card is the durable path
  // back to the accepted full draft after transient viewer state is lost on reload.
  return matterArtifactVersions
    .filter(
      (matterArtifactVersion) =>
        matterArtifactVersion.matter_artifact_id !== selectedMatterArtifactId ||
        (matterArtifactVersion.status === MatterArtifactVersionStatus.COMPLETE &&
          matterArtifactVersion.revision_kind === MatterArtifactVersionRevisionKind.SELECTED_PASSAGE_EDIT)
    )
    .map((matterArtifactVersion) => matterArtifactVersion.id)
}

function appendMessageProgressRow(messages: AgentV2Message[], messageId: string | null, row: AgentV2ProgressRow): AgentV2Message[] {
  return updateMessageWorkLog(messages, messageId, (workLogItems) => appendProgressRow(workLogItems, row))
}

function markMessageProgressRowsComplete(messages: AgentV2Message[], messageId: string | null): AgentV2Message[] {
  return updateMessageWorkLog(messages, messageId, markProgressRowsComplete)
}

function markMessageWorkLogError(messages: AgentV2Message[], messageId: string | null): AgentV2Message[] {
  return updateMessageWorkLog(messages, messageId, markWorkLogError)
}

function updateMessageWorkLog(
  messages: AgentV2Message[],
  messageId: string | null,
  updateWorkLogItems: (workLogItems: AgentV2WorkLogItem[]) => AgentV2WorkLogItem[]
): AgentV2Message[] {
  if (!messageId) return messages
  return messages.map((message) => (message.id === messageId ? { ...message, workLogItems: updateWorkLogItems(message.workLogItems ?? []) } : message))
}

function appendProgressRow(workLogItems: AgentV2WorkLogItem[], row: AgentV2ProgressRow): AgentV2WorkLogItem[] {
  return sortWorkLogItems([...markProgressRowsComplete(workLogItems), row])
}

function markProgressRowsComplete(workLogItems: AgentV2WorkLogItem[]): AgentV2WorkLogItem[] {
  return workLogItems.map((item) => (item.kind === 'update' && item.status === 'active' ? { ...item, status: 'complete' } : item))
}

function markProgressRowsCancelled(workLogItems: AgentV2WorkLogItem[]): AgentV2WorkLogItem[] {
  return workLogItems.map((item) => (item.kind === 'update' && item.status === 'active' ? { ...item, status: 'cancelled' } : item))
}

function markWorkLogError(workLogItems: AgentV2WorkLogItem[]): AgentV2WorkLogItem[] {
  return workLogItems.map((item) => (item.status === 'active' ? { ...item, status: 'error' } : item))
}

function sortWorkLogItems(workLogItems: AgentV2WorkLogItem[]): AgentV2WorkLogItem[] {
  return [...workLogItems].sort((a, b) => a.sequence - b.sequence)
}

function enqueueAssistantText(messages: AgentV2Message[], streamingMessageId: string | null, delta: string): AgentV2Message[] {
  if (!streamingMessageId) return messages
  const tokens = splitTextIntoTypingTokens(delta)
  if (tokens.length === 0) return messages
  return messages.map((message) =>
    message.id === streamingMessageId
      ? {
          ...message,
          pendingTextTokens: [...(message.pendingTextTokens ?? []), ...tokens],
          isTextComplete: false,
          isStreaming: true,
        }
      : message
  )
}

function completeAssistantMessage(messages: AgentV2Message[], streamingMessageId: string | null): AgentV2Message[] {
  if (!streamingMessageId) return messages

  return messages.map((message) => {
    if (message.id !== streamingMessageId) return message

    if (!message.pendingTextTokens?.length) {
      return { ...message, pendingTextTokens: [], isTextComplete: true, isStreaming: false }
    }

    return {
      ...message,
      isTextComplete: true,
      isStreaming: true,
    }
  })
}

function flushMessageTextTokens(messages: AgentV2Message[], messageId: string, tokenCount: number): AgentV2Message[] {
  const safeTokenCount = Math.max(1, tokenCount)

  return messages.map((message) => {
    if (message.id !== messageId || !message.pendingTextTokens?.length) return message

    const tokensToFlush = message.pendingTextTokens.slice(0, safeTokenCount)
    const remainingTokens = message.pendingTextTokens.slice(safeTokenCount)
    const nextText = message.text + tokensToFlush.join('')

    if (remainingTokens.length === 0 && message.isTextComplete) {
      return {
        ...message,
        text: nextText,
        pendingTextTokens: [],
        isTextComplete: true,
        isStreaming: false,
      }
    }

    return {
      ...message,
      text: nextText,
      pendingTextTokens: remainingTokens,
      isStreaming: true,
    }
  })
}

function finalizeHistoricalMessages(messages: AgentV2Message[], openTurnId: string | null): AgentV2Message[] {
  return messages
    .map((message) => {
      if (message.role !== 'assistant') return message

      return {
        ...message,
        pendingTextTokens: [],
        isTextComplete: true,
        isStreaming: false,
      }
    })
    .filter(
      (message) =>
        message.role === 'user' ||
        message.text ||
        message.errorText ||
        (message.workLogItems?.length ?? 0) > 0 ||
        (message.matterArtifactVersionIds?.length ?? 0) > 0 ||
        (message.tableCardIds?.length ?? 0) > 0 ||
        message.turnId === openTurnId ||
        message.isStopped
    )
}

function getStreamingMessageIdAfterFlush(messages: AgentV2Message[], messageId: string, currentStreamingMessageId: string | null): string | null {
  if (messageId !== currentStreamingMessageId) return currentStreamingMessageId

  const message = messages.find((currentMessage) => currentMessage.id === messageId)
  if (!message) return null

  if ((message.pendingTextTokens?.length ?? 0) > 0 || !message.isTextComplete) return currentStreamingMessageId
  return null
}

function hasPendingTextTokens(messages: AgentV2Message[], messageId: string | null): boolean {
  if (!messageId) return false
  const message = messages.find((currentMessage) => currentMessage.id === messageId)
  return (message?.pendingTextTokens?.length ?? 0) > 0
}

function foldPendingAssistantText(messages: AgentV2Message[], messageId: string | null): AgentV2Message[] {
  if (!messageId) return messages
  return messages.map((message) => {
    if (message.id !== messageId || !message.pendingTextTokens?.length) return message
    return {
      ...message,
      text: message.text + message.pendingTextTokens.join(''),
      pendingTextTokens: [],
      isTextComplete: true,
      isStreaming: false,
    }
  })
}

function clearAssistantPendingText(messages: AgentV2Message[], messageId: string | null): AgentV2Message[] {
  if (!messageId) return messages
  return messages.map((message) =>
    message.id === messageId
      ? {
          ...message,
          pendingTextTokens: [],
          isStreaming: false,
        }
      : message
  )
}

function stopAssistantMessage(messages: AgentV2Message[], streamingMessageId: string | null, activeMessageId: string | null): AgentV2Message[] {
  if (!activeMessageId) return messages
  return messages.map((message) => {
    if (message.id !== activeMessageId) return message

    const hasStartedText = message.text.length > 0 || (message.pendingTextTokens?.length ?? 0) > 0
    const updateProgressRows = hasStartedText ? markProgressRowsComplete : markProgressRowsCancelled

    return {
      ...message,
      workLogItems: updateProgressRows(message.workLogItems ?? []),
      pendingTextTokens: message.id === streamingMessageId ? [] : message.pendingTextTokens,
      isStreaming: false,
      isStopped: true,
    }
  })
}

function failAssistantMessage(messages: AgentV2Message[], activeMessageId: string | null, errorText: string): AgentV2Message[] {
  // No active assistant message: the caller surfaces the error via the inline errorMessage
  // strip — never fabricate an assistant reply in the transcript (ENG-7075)
  if (!activeMessageId) return messages

  return messages.map((message) =>
    message.id === activeMessageId
      ? {
          ...message,
          isError: true,
          errorText,
          isStreaming: false,
        }
      : message
  )
}

function splitTextIntoTypingTokens(text: string): string[] {
  return text.match(/\S+\s*|\s+/g) ?? []
}

function canonicalizeTurnMessageIds(state: AgentV2TranscriptState, turnId: string): AgentV2TranscriptState {
  const assistantIndex = state.messages.findIndex((message) => message.role === 'assistant' && message.turnId === turnId)
  if (assistantIndex < 0) return state

  const assistantMessage = state.messages[assistantIndex]
  const canonicalAssistantMessageId = `${turnId}:assistant`
  const precedingMessage = state.messages[assistantIndex - 1]
  const shouldCanonicalizePrecedingUser =
    precedingMessage?.role === 'user' &&
    (!precedingMessage.turnId || precedingMessage.turnId === turnId) &&
    precedingMessage.clientRequestId === assistantMessage.clientRequestId

  return {
    ...state,
    messages: state.messages.map((message, index) => {
      if (message.role === 'assistant' && message.turnId === turnId) return { ...message, id: canonicalAssistantMessageId }
      if ((message.role === 'user' && message.turnId === turnId) || (index === assistantIndex - 1 && shouldCanonicalizePrecedingUser)) {
        return { ...message, id: `${turnId}:user`, turnId }
      }
      return message
    }),
    activeAssistantMessageId: state.activeAssistantMessageId === assistantMessage.id ? canonicalAssistantMessageId : state.activeAssistantMessageId,
    streamingMessageId: state.streamingMessageId === assistantMessage.id ? canonicalAssistantMessageId : state.streamingMessageId,
  }
}

function preserveLiveMessagesMissingFromSnapshot(
  localState: AgentV2TranscriptState,
  hydratedState: AgentV2TranscriptState,
  conversation: AgentV2ConversationDetailResponse
): AgentV2TranscriptState {
  const snapshotTurnIds = new Set(conversation.turns.map((turn) => turn.id))
  const turnByClientRequestId = new Map(conversation.turns.map((turn) => [turn.client_request_id, turn]))
  const localStartedAtByTurnId = new Map(
    localState.messages.filter((message) => message.runStartedAt).map((message) => [message.turnId, message.runStartedAt!])
  )
  const missingMessages: Array<{ beforeTurnId: string | null; message: AgentV2Message }> = []
  let messages = hydratedState.messages

  for (const [localIndex, localMessage] of localState.messages.entries()) {
    const nextMatchedTurn = localState.messages
      .slice(localIndex + 1)
      .map((message) => (message.clientRequestId ? turnByClientRequestId.get(message.clientRequestId) : undefined))
      .find(Boolean)
    const localStartedAt = localStartedAtByTurnId.get(localMessage.turnId)
    const nextNewerTurn = localStartedAt
      ? conversation.turns.find(
          (turn) =>
            Date.parse(turn.started_at) > Date.parse(localStartedAt) ||
            (Date.parse(turn.started_at) === Date.parse(localStartedAt) && turn.id > (localMessage.turnId ?? ''))
        )
      : undefined
    const preserveMessage = () => missingMessages.push({ beforeTurnId: nextMatchedTurn?.id ?? nextNewerTurn?.id ?? null, message: localMessage })

    if (localMessage.turnId) {
      if (!snapshotTurnIds.has(localMessage.turnId)) preserveMessage()
      continue
    }

    const matchedTurn = localMessage.clientRequestId ? turnByClientRequestId.get(localMessage.clientRequestId) : undefined
    if (!matchedTurn) {
      preserveMessage()
      continue
    }
    if (localMessage.role !== 'assistant') continue

    const hydratedAssistant = messages.find((message) => message.role === 'assistant' && message.turnId === matchedTurn.id)
    if (!hydratedAssistant) {
      preserveMessage()
      continue
    }

    messages = messages.map((message) => {
      if (message.id !== hydratedAssistant.id) return message

      const preserveTerminalPartial = Boolean(matchedTurn.ended_at && (matchedTurn.did_fail || matchedTurn.did_cancel) && !message.text && localMessage.text)
      if (matchedTurn.ended_at && !preserveTerminalPartial) return message
      const workLogItems = reconcileRecoveredWorkLogItems(localMessage.workLogItems ?? [], message.workLogItems ?? [])
      return {
        ...message,
        text: reconcileRecoveredAssistantText(localMessage.text, message.text),
        tableCardIds: [...new Set([...(localMessage.tableCardIds ?? []), ...(message.tableCardIds ?? [])])],
        matterArtifactVersionIds: [...new Set([...(localMessage.matterArtifactVersionIds ?? []), ...(message.matterArtifactVersionIds ?? [])])],
        workLogItems: matchedTurn.did_fail ? markWorkLogError(workLogItems) : matchedTurn.did_cancel ? markProgressRowsCancelled(workLogItems) : workLogItems,
        isStreaming: matchedTurn.ended_at ? false : localMessage.isStreaming,
        isTextComplete: matchedTurn.ended_at ? true : localMessage.isTextComplete,
      }
    })
  }

  const missingByFollowingTurnId = new Map<string, AgentV2Message[]>()
  const trailingMessages: AgentV2Message[] = []
  for (const missing of missingMessages) {
    if (!missing.beforeTurnId) {
      trailingMessages.push(missing.message)
      continue
    }
    missingByFollowingTurnId.set(missing.beforeTurnId, [...(missingByFollowingTurnId.get(missing.beforeTurnId) ?? []), missing.message])
  }
  if (missingMessages.length > 0) {
    const insertedBeforeTurnIds = new Set<string>()
    messages = messages.flatMap((message) => {
      if (!message.turnId || insertedBeforeTurnIds.has(message.turnId)) return [message]
      insertedBeforeTurnIds.add(message.turnId)
      return [...(missingByFollowingTurnId.get(message.turnId) ?? []), message]
    })
    messages = [...messages, ...trailingMessages]
  }
  let nextState = { ...hydratedState, messages }
  for (const turn of conversation.turns) nextState = canonicalizeTurnMessageIds(nextState, turn.id)
  return nextState
}

function preserveOpenLivePointers(
  localState: AgentV2TranscriptState,
  hydratedState: AgentV2TranscriptState,
  conversation: AgentV2ConversationDetailResponse
): AgentV2TranscriptState {
  const localActiveMessage = localState.messages.find((message) => message.id === localState.activeAssistantMessageId)
  if (!localActiveMessage) return hydratedState

  const durableTurn = localActiveMessage.turnId
    ? conversation.turns.find((turn) => turn.id === localActiveMessage.turnId)
    : localActiveMessage.clientRequestId
    ? conversation.turns.find((turn) => turn.client_request_id === localActiveMessage.clientRequestId)
    : undefined
  if (durableTurn?.ended_at) return hydratedState

  const nextActiveMessage = localActiveMessage.turnId
    ? hydratedState.messages.find((message) => message.role === 'assistant' && message.turnId === localActiveMessage.turnId)
    : localActiveMessage.clientRequestId
    ? hydratedState.messages.find((message) => message.role === 'assistant' && message.clientRequestId === localActiveMessage.clientRequestId)
    : hydratedState.messages.find((message) => message.id === localActiveMessage.id)
  if (!nextActiveMessage) return hydratedState

  return {
    ...hydratedState,
    messages: hydratedState.messages.map((message) =>
      message.id === nextActiveMessage.id
        ? {
            ...message,
            isStreaming: localActiveMessage.isStreaming,
            isTextComplete: localActiveMessage.isTextComplete,
          }
        : message
    ),
    activeAssistantMessageId: nextActiveMessage.id,
    streamingMessageId: localState.streamingMessageId ? nextActiveMessage.id : null,
  }
}

function isOlderDurableSnapshot(currentUpdatedAt: string | null, incomingUpdatedAt: string): boolean {
  return currentUpdatedAt !== null && Date.parse(incomingUpdatedAt) < Date.parse(currentUpdatedAt)
}

function isTerminalEventType(type: AgentV2ServerEventType): boolean {
  return type === AgentV2ServerEventType.RUN_COMPLETED || type === AgentV2ServerEventType.RUN_FAILED || type === AgentV2ServerEventType.RUN_CANCELLED
}
